import { useEffect, useId, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import type { Theme } from '@mui/material/styles';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { Popper } from 'ui/popper';
import { Typography } from 'ui/typography';

import type { PopperProps } from 'ui/popper';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';

import { useClickOutside } from 'hooks/use-click-outside';
import { useFocusOnOpen } from 'hooks/use-focus-on-open';

import composeRefs from 'helpers/compose-refs';

import Icons from 'assets/icons';

import {
  MARK_NEW_CHAT_CONFIRMATION_BODY,
  MARK_NEW_CHAT_CONFIRMATION_CLOSE_ARIA_LABEL,
  MARK_NEW_CHAT_CONFIRMATION_HISTORY_LOCATION,
  MARK_NEW_CHAT_CONFIRMATION_SUBMIT_LABEL,
  MARK_NEW_CHAT_CONFIRMATION_TITLE,
  MARK_PANEL_Z_INDEX_CLASS,
} from '../mark-panel.constants';

const POPOVER_WIDTH_PX = 304;
const ANCHOR_GAP_PX = 2;
const VIEWPORT_MARGIN_PX = 8;

const POPPER_MODIFIERS: PopperProps['modifiers'] = [
  { name: 'offset', options: { offset: [0, ANCHOR_GAP_PX] } },
  { name: 'preventOverflow', options: { padding: VIEWPORT_MARGIN_PX } },
];

export interface MarkNewChatConfirmationProps {
  anchorElement: HTMLElement | null | undefined;
  onConfirm: () => void;
  onDismiss: () => void;
}

const MarkNewChatConfirmation = ({
  anchorElement,
  onConfirm,
  onDismiss,
}: MarkNewChatConfirmationProps) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>(
    null
  );
  const composedPopoverRef = useMemo(
    () => composeRefs(popoverRef, setPopoverElement),
    []
  );
  const titleId = useId();
  const isAnchorConnected = !!anchorElement?.isConnected;
  const isMounted = popoverElement !== null;

  useFocusOnOpen(popoverRef, isMounted);
  useClickOutside(popoverRef, onDismiss, isMounted);

  useEffect(() => {
    if (!isMounted) return undefined;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onDismiss();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMounted, onDismiss]);

  if (!isAnchorConnected) return null;

  return (
    <Popper
      open
      anchorEl={anchorElement}
      placement="bottom-start"
      modifiers={POPPER_MODIFIERS}
      className={classNames('animate-fade-in', MARK_PANEL_Z_INDEX_CLASS)}
    >
      <Stack
        ref={composedPopoverRef}
        role="dialog"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid="mark-new-chat-confirmation"
        sx={{
          position: 'relative',
          width: POPOVER_WIDTH_PX,
          gap: 2,
          p: 2.5,
          borderRadius: '0.75rem',
          bgcolor: 'tokens.navy.50',
          boxShadow: (theme: Theme) =>
            `0 0 1px ${theme.palette.tokens.navy[500]}, ${theme.shadows[1]}`,
          outline: 'none',
        }}
      >
        <IconButton
          variant="neutral-text-link"
          size="sm"
          icon={<Icons.Close aria-hidden="true" />}
          aria-label={MARK_NEW_CHAT_CONFIRMATION_CLOSE_ARIA_LABEL}
          onClick={onDismiss}
          className="absolute right-3 top-3"
          data-testid="mark-new-chat-close-button"
        />

        <Stack sx={{ gap: 0.5 }}>
          <Typography
            id={titleId}
            variant="h5"
            component="h3"
            sx={{ fontWeight: 'fontWeightBold' }}
          >
            {MARK_NEW_CHAT_CONFIRMATION_TITLE}
          </Typography>

          <Typography variant="body2" sx={{ color: 'tokens.gray.700' }}>
            {MARK_NEW_CHAT_CONFIRMATION_BODY}{' '}
            <Box component="span" sx={{ fontWeight: 'fontWeightMedium' }}>
              {MARK_NEW_CHAT_CONFIRMATION_HISTORY_LOCATION}
            </Box>
          </Typography>
        </Stack>

        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <Button
            type="button"
            variant="reserve-filled"
            size="xs"
            onClick={onConfirm}
            data-testid="new-chat-submit-button"
          >
            {MARK_NEW_CHAT_CONFIRMATION_SUBMIT_LABEL}
          </Button>
        </Stack>
      </Stack>
    </Popper>
  );
};

export default MarkNewChatConfirmation;
